import { useEffect, useState } from "react";
import { detectPrint, sampleForDetection } from "./detect";
import { enhancePrint } from "./enhance";
import { DEFAULT_QUAD, type Quad } from "./geometry";
import { canvasToJpeg, imageDataToCanvas, nextPaint, rotateJpeg, toCanvas } from "./image";
import { warpQuad } from "./warp";

export type CaptureStep = "camera" | "corners" | "review";

export interface ScanResult {
  original: Blob;
  enhanced: Blob;
}

export function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}

/**
 * The camera → corners → straighten-and-enhance part of scanning a print,
 * shared by the Scan page (which saves into an album) and the new-post form
 * (which attaches the scan to a post).
 */
export function useScanCapture() {
  const [step, setStep] = useState<CaptureStep>("camera");
  const [frame, setFrame] = useState<HTMLCanvasElement | null>(null);
  const [frameBlob, setFrameBlob] = useState<Blob | null>(null);
  const [quad, setQuad] = useState<Quad>(DEFAULT_QUAD);
  const [detected, setDetected] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [useEnhanced, setUseEnhanced] = useState(true);
  const [working, setWorking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const frameUrl = useObjectUrl(frameBlob);
  const shown = result ? (useEnhanced ? result.enhanced : result.original) : null;
  const shownUrl = useObjectUrl(shown);

  async function onCapture(canvas: HTMLCanvasElement) {
    setError(null);
    setFrame(canvas);
    const sample = sampleForDetection(canvas, canvas.width, canvas.height, document.createElement("canvas"));
    const found = sample ? detectPrint(sample) : null;
    setQuad(found ?? DEFAULT_QUAD);
    setDetected(Boolean(found));
    // The corner editor shows a small copy; the full frame stays in memory
    // for the warp.
    setFrameBlob(await canvasToJpeg(toCanvas(canvas, canvas.width, canvas.height, 1400), 0.85));
    setStep("corners");
  }

  async function straighten() {
    if (!frame) return;
    setError(null);
    setWorking("Straightening and enhancing…");
    await nextPaint();
    try {
      const src = frame.getContext("2d")!.getImageData(0, 0, frame.width, frame.height);
      const flat = warpQuad(src, quad);
      const original = await canvasToJpeg(imageDataToCanvas(flat));
      const enhanced = await canvasToJpeg(imageDataToCanvas(enhancePrint(flat)));
      setResult({ original, enhanced });
      setUseEnhanced(true);
      setStep("review");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWorking(null);
    }
  }

  async function rotate() {
    if (!result) return;
    setWorking("Rotating…");
    try {
      const [original, enhanced] = await Promise.all([rotateJpeg(result.original), rotateJpeg(result.enhanced)]);
      setResult({ original, enhanced });
    } finally {
      setWorking(null);
    }
  }

  function reset() {
    setFrame(null);
    setFrameBlob(null);
    setResult(null);
    setError(null);
    setStep("camera");
  }

  return {
    step,
    setStep,
    quad,
    setQuad,
    detected,
    result,
    shown,
    useEnhanced,
    setUseEnhanced,
    working,
    setWorking,
    error,
    setError,
    frameUrl,
    shownUrl,
    onCapture,
    straighten,
    rotate,
    reset,
  };
}

export type ScanCapture = ReturnType<typeof useScanCapture>;
